/**
 * Hablarle desde el botón de los auriculares.
 *
 * Sin sacar el móvil del bolsillo: pulsas el botón, hablas, vuelves a pulsar y
 * se manda. Es la diferencia entre poder usarlo conduciendo o andando por la
 * calle y tener que pararse a sujetar el dedo en la pantalla.
 *
 * **Lo que una página puede oír de un auricular, y lo que no.**
 *
 * No hay forma de que el navegador vea los botones de un aparato Bluetooth.
 * Lo único que llega es lo que el sistema traduce a teclas de MEDIOS —
 * reproducir, pausar, siguiente— y eso sólo si esta página es la que está
 * sonando. O sea que para escuchar el botón hay que estar reproduciendo algo.
 *
 * Por eso esto suena. Un tono a volumen casi cero, en bucle, generado aquí
 * mismo — no se descarga nada. No es un truco elegante y es el que hay: sin
 * audio no hay sesión de medios, y sin sesión de medios el botón se lo lleva
 * Spotify.
 *
 * **Lo que se pierde mientras está encendido:** el botón deja de controlar la
 * música. Por eso se enciende y se apaga a mano en vez de estar siempre puesto.
 *
 * **Y lo que no se puede saber desde aquí:** si el botón concreto de una funda
 * manda una tecla de medios o hace algo propio de su marca. Se prueba y se ve.
 * Los auriculares normales —el pellizco, el botón de la cápsula— mandan
 * reproducir/pausar y funcionan seguro.
 */

/**
 * Treinta segundos de casi silencio, hechos aquí.
 *
 * Dos decisiones, y las dos costaron una prueba fallida:
 *
 * **Casi, no del todo.** Silencio absoluto no vale: si no sale nada por el
 * altavoz, algunos navegadores no abren sesión de medios. Con amplitud 1 sobre
 * 128 no se oye y cuenta como sonido.
 *
 * **Y treinta segundos, no uno.** Empezó durando un segundo en bucle y el botón
 * seguía yéndose a YouTube. El motivo: los navegadores del móvil sólo publican
 * una sesión de medios en el sistema si lo que suena dura lo bastante como para
 * ser contenido de verdad —unos segundos— porque si no, cualquier página con un
 * pitido de aviso secuestraría el botón de los auriculares de todo el móvil. Un
 * bucle de un segundo no pasa ese listón por mucho que se repita.
 *
 * Treinta segundos a 8 kHz y 8 bits son 240 KB en memoria. No se descarga nada.
 */
function tonoCasiMudo(): string {
  const hz = 8000;
  const muestras = hz * 30;
  const bytes = new Uint8Array(44 + muestras);
  const vista = new DataView(bytes.buffer);

  const texto = (posicion: number, valor: string) => {
    for (let i = 0; i < valor.length; i++) vista.setUint8(posicion + i, valor.charCodeAt(i));
  };

  texto(0, "RIFF");
  vista.setUint32(4, 36 + muestras, true);
  texto(8, "WAVEfmt ");
  vista.setUint32(16, 16, true);
  vista.setUint16(20, 1, true); // PCM
  vista.setUint16(22, 1, true); // mono
  vista.setUint32(24, hz, true);
  vista.setUint32(28, hz, true);
  vista.setUint16(32, 1, true);
  vista.setUint16(34, 8, true); // 8 bits
  texto(36, "data");
  vista.setUint32(40, muestras, true);

  // 128 es el cero de un PCM de 8 bits sin signo. Una unidad arriba y abajo.
  for (let i = 0; i < muestras; i++) vista.setUint8(44 + i, 128 + (i % 2));

  return URL.createObjectURL(new Blob([bytes], { type: "audio/wav" }));
}

export function sePuedeManosLibres(): boolean {
  return typeof navigator !== "undefined" && "mediaSession" in navigator;
}

export interface ManosLibres {
  parar(): void;
  /**
   * Se va a abrir el micrófono: calla el tono, pero no sueltes el botón.
   *
   * Hace falta porque el tono y el micrófono se pelean por el mismo auricular.
   * Un auricular Bluetooth no reproduce y graba a la vez en buena calidad: al
   * abrir el micrófono, Android cambia de perfil, y con nuestro tono
   * empeñándose en sonar la grabación salía MUDA — quince segundos de silencio.
   *
   * Callar sin soltar es posible porque una sesión de medios en pausa sigue
   * siendo la dueña del botón: es lo que hace cualquier reproductor de música
   * cuando lo pausas, y la siguiente pulsación le llega igual.
   */
  micAbierto(): void;
  /** Micrófono cerrado: el tono vuelve. */
  micCerrado(): void;
}

/**
 * Enciende la escucha del botón. Devuelve con qué apagarla.
 *
 * `alPulsar` se llama una vez por pulsación: es quien decide si eso significa
 * empezar a grabar o terminar. Aquí no se sabe ni hace falta.
 */
export async function manosLibres(alPulsar: () => void): Promise<ManosLibres> {
  const url = tonoCasiMudo();
  const audio = new Audio(url);
  audio.loop = true;
  audio.volume = 0.001;

  await audio.play();

  /**
   * Y se espera a que esté sonando de verdad antes de coger el botón.
   *
   * `play()` vuelve en cuanto arranca, no cuando el sistema se ha enterado. Si
   * se registran los manejadores antes de eso, el móvil todavía tiene por
   * dueño del botón a lo último que sonó — que es exactamente cómo acababa
   * poniéndose un vídeo de YouTube al pellizcar.
   */
  await new Promise((listo) => setTimeout(listo, 350));

  /**
   * Si algo para el tono, se vuelve a poner en marcha inmediatamente.
   *
   * Es lo que hacía que el segundo pellizco no parase de grabar: **abrir el
   * micrófono para de golpe lo que esté sonando**. El tono moría, con él la
   * sesión de medios, y la siguiente pulsación ya no llegaba aquí — se la
   * volvía a quedar el móvil.
   *
   * No basta con arrancarlo cuando llega una pulsación, porque justo entonces
   * ya no llega ninguna. Tiene que reaccionar solo, en cuanto se pare y sin
   * esperar a nadie.
   */
  let encendido = true;
  let grabando = false;
  audio.addEventListener("pause", () => {
    // Mientras se graba, la pausa la hemos pedido nosotros: dejarla estar.
    if (encendido && !grabando) void audio.play().catch(() => {});
  });

  const sesion = navigator.mediaSession;

  /**
   * Lo que se ve en la pantalla de bloqueo mientras está encendido.
   *
   * No es decoración: es la única señal de que el botón está secuestrado. Sin
   * esto, dentro de dos horas pulsas para poner música y te sale grabando.
   */
  if ("MediaMetadata" in window) {
    sesion.metadata = new MediaMetadata({
      title: "BILBOBOT · manos libres",
      artist: "Pulsa el botón para hablar",
    });
  }

  /**
   * Pausar tiene que seguir sonando.
   *
   * El botón manda «pausa», y si se le hace caso el audio se para, la sesión de
   * medios se cierra y la siguiente pulsación ya no llega aquí. Funcionaría una
   * vez. Así que se atiende la pulsación y se vuelve a arrancar el tono.
   */
  const atender = () => {
    alPulsar();
    if (audio.paused) void audio.play().catch(() => {});
    sesion.playbackState = "playing";
  };

  for (const accion of ["play", "pause", "stop"] as const) {
    try {
      sesion.setActionHandler(accion, atender);
    } catch {
      // Un navegador que no conozca una acción no puede impedir las otras.
    }
  }
  sesion.playbackState = "playing";

  return {
    micAbierto() {
      grabando = true;
      /**
       * Se suelta el canal de audio ENTERO, no sólo se pausa.
       *
       * Pausar no bastaba: el elemento seguía agarrado al auricular y la
       * grabación salía muda igual —veintitrés segundos de silencio—. Un
       * elemento de audio en pausa sigue teniendo cogido el aparato; para que
       * Android pueda cambiarlo a modo micrófono hay que soltarlo del todo.
       *
       * Y el botón se pierde igualmente durante la grabación —lo coge la
       * telefonía, no hay vuelta de hoja—, así que no se gana nada por
       * conservar el tono. Por eso se corta a lo bruto y se recompone después.
       */
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      sesion.playbackState = "paused";
    },

    micCerrado() {
      grabando = false;
      audio.src = url;
      sesion.playbackState = "playing";
      void audio.play().catch(() => {});
    },

    parar() {
      encendido = false;
      grabando = false;
      for (const accion of ["play", "pause", "stop"] as const) {
        try {
          sesion.setActionHandler(accion, null);
        } catch {
          /* da igual */
        }
      }
      sesion.playbackState = "none";
      audio.pause();
      audio.src = "";
      URL.revokeObjectURL(url);
    },
  };
}
